import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../Auth/AuthContext';
import authFetch from '../../helpers/authFetch';
import {
  applyAction,
  cardViewsOf,
  createSession,
  freeMulligansFor,
  popUndo,
  pushUndo,
  toSessionEntries,
} from '../../../shared/playtest';
import { loadWholeDeck } from './loadWholeDeck';

/** Full 32-bit seed range; seeds are chosen here, never inside the pure engine. */
const SEED_RANGE = 0x100000000;

const LOAD_ERROR_MESSAGE = 'Could not load this deck. Check your connection and try again.';

/** Card details still loading after this long are abandoned and offer Retry. */
export const CARD_DETAIL_TIMEOUT_MS = 15000;

const NO_SESSIONS = new Map();

const PlaytestSessionContext = createContext(null);

const newSeed = () => Math.floor(Math.random() * SEED_RANGE);

const emptySession = (loadToken) => ({
  loadToken,
  status: 'loading',
  error: null,
  deckType: null,
  cardViews: new Map(),
  engine: null,
  tier: null,
  details: new Map(),
  undo: [],
});

/**
 * Keeps one playtest session per signed-in user and collection id, in memory. Sessions survive
 * route changes within the app. They are cleared on sign-out and on any change of user, and a load
 * still in flight for the previous user is dropped when it completes.
 * Updaters return `undefined` for a no-op, so the map is left untouched.
 */
export const PlaytestSessionProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const userId = isAuthenticated && user ? user.id : null;
  // Outer key is the user id, inner key the collection id. Only the signed-in user's inner map is exposed.
  const [storedSessions, setSessions] = useState(() => new Map());
  // Mirrors the state map and is written in the same call. Actions read it synchronously, so an
  // action's result is known at dispatch time and two calls in one event see each other.
  const sessionsRef = useRef(storedSessions);
  const sessions = userId === null ? NO_SESSIONS : (storedSessions.get(userId) ?? NO_SESSIONS);
  const epochRef = useRef(0);
  const loadTokenRef = useRef(0);
  const clearedForUserRef = useRef(userId);

  const commit = useCallback((next) => {
    sessionsRef.current = next;
    setSessions(next);
  }, []);

  useEffect(() => {
    const previousUserId = clearedForUserRef.current;
    if (previousUserId === userId) return;
    clearedForUserRef.current = userId;
    // Effects run child first, so the new user's first load may already be in flight. Only the
    // previous user's sessions are dropped; a sign-out also drops that user's in-flight loads.
    if (previousUserId === null || !sessionsRef.current.has(previousUserId)) return;
    if (userId === null) epochRef.current += 1;
    const next = new Map(sessionsRef.current);
    next.delete(previousUserId);
    commit(next);
  }, [userId, commit]);

  /**
   * `update` gets this deck's current session for the signed-in user and returns the next one,
   * or undefined for no change.
   */
  const updateSession = useCallback(
    (collectionId, update) => {
      if (userId === null) return;
      const prev = sessionsRef.current;
      const userSessions = prev.get(userId) ?? NO_SESSIONS;
      const next = update(userSessions.get(collectionId));
      if (next === undefined) return;
      const map = new Map(prev);
      map.set(userId, new Map(userSessions).set(collectionId, next));
      commit(map);
    },
    [commit, userId]
  );

  const startDeckLoad = useCallback(
    (rawId) => {
      const collectionId = String(rawId);
      loadTokenRef.current += 1;
      const loadToken = loadTokenRef.current;
      const epoch = epochRef.current;
      const isCurrent = (session) => session && session.loadToken === loadToken;

      updateSession(collectionId, () => emptySession(loadToken));
      loadWholeDeck(collectionId).then(
        ({ rows, deckType }) => {
          const current = sessionsRef.current.get(userId)?.get(collectionId);
          if (epoch !== epochRef.current || !isCurrent(current)) return;
          // The seed and the engine are made here, outside the updater, so the updater stays pure.
          const engine = createSession({
            entries: toSessionEntries(rows),
            seed: newSeed(),
            freeMulligans: freeMulligansFor(deckType),
          });
          updateSession(collectionId, (session) => ({
            ...session,
            status: 'ready',
            deckType,
            engine,
            cardViews: cardViewsOf(rows),
          }));
        },
        () => {
          if (epoch !== epochRef.current) return;
          updateSession(collectionId, (session) =>
            isCurrent(session) ? { ...session, status: 'error', error: LOAD_ERROR_MESSAGE } : undefined
          );
        }
      );
    },
    [updateSession, userId]
  );

  /**
   * Applies engine actions in order as one undo step. All of them apply or none does: the first
   * rejected action returns its result and changes nothing and pushes nothing. Returns 'ok', the
   * rejection, or null when the deck has no ready session.
   */
  const dispatchActions = useCallback(
    (rawId, actions) => {
      const collectionId = String(rawId);
      let outcome = null;
      updateSession(collectionId, (session) => {
        if (!session || !session.engine) return undefined;
        let state = session.engine;
        for (const action of actions) {
          const applied = applyAction(state, action);
          if (applied.result !== 'ok') {
            outcome = applied.result;
            return undefined;
          }
          state = applied.state;
        }
        outcome = 'ok';
        return { ...session, engine: state, undo: pushUndo(session.undo, session.engine) };
      });
      return outcome;
    },
    [updateSession]
  );

  /** Applies one engine action; see dispatchActions for the result. */
  const dispatchAction = useCallback((rawId, action) => dispatchActions(rawId, [action]), [dispatchActions]);

  /** Restores the engine state from before the newest successful action. */
  const undoLast = useCallback(
    (rawId) => {
      updateSession(String(rawId), (session) => {
        const popped = session ? popUndo(session.undo) : undefined;
        if (!popped) return undefined;
        return { ...session, engine: popped.state, undo: popped.stack };
      });
    },
    [updateSession]
  );

  const setTier = useCallback(
    (rawId, tier) => {
      updateSession(String(rawId), (session) => (session ? { ...session, tier } : undefined));
    },
    [updateSession]
  );

  /** Discards this deck's session; the next visit starts fresh at the tier selector. */
  const endSession = useCallback(
    (rawId) => {
      const collectionId = String(rawId);
      const prev = sessionsRef.current;
      const userSessions = prev.get(userId);
      if (!userSessions?.has(collectionId)) return;
      const remaining = new Map(userSessions);
      remaining.delete(collectionId);
      const map = new Map(prev);
      map.set(userId, remaining);
      commit(map);
    },
    [commit, userId]
  );

  const requestCardDetail = useCallback(
    (rawId, cardId) => {
      const collectionId = String(rawId);
      const epoch = epochRef.current;
      const controller = new AbortController();
      let settled = false;
      let timer = null;
      const setDetail = (detail) =>
        updateSession(collectionId, (session) => {
          if (!session || epoch !== epochRef.current) return undefined;
          const details = new Map(session.details);
          details.set(cardId, detail);
          return { ...session, details };
        });
      // The first outcome of this request counts. A response that arrives after the timeout has
      // already set the error is ignored. Retry is offered only after an error, so it starts a
      // new request with its own outcome.
      const settle = (detail) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        setDetail(detail);
      };

      setDetail({ status: 'loading' });
      timer = setTimeout(() => {
        controller.abort();
        settle({ status: 'error' });
      }, CARD_DETAIL_TIMEOUT_MS);
      authFetch(`/cards/${cardId}`, { signal: controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(`Card ${cardId} failed with status ${response.status}`);
          return response.json();
        })
        .then(
          (card) => settle({ status: 'ready', card }),
          () => settle({ status: 'error' })
        );
    },
    [updateSession]
  );

  const value = useMemo(
    () => ({
      sessions,
      startDeckLoad,
      dispatchAction,
      dispatchActions,
      undoLast,
      setTier,
      endSession,
      requestCardDetail,
    }),
    [sessions, startDeckLoad, dispatchAction, dispatchActions, undoLast, setTier, endSession, requestCardDetail]
  );

  return <PlaytestSessionContext.Provider value={value}>{children}</PlaytestSessionContext.Provider>;
};

PlaytestSessionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const usePlaytestSessions = () => {
  const context = useContext(PlaytestSessionContext);
  if (!context) {
    throw new Error('usePlaytestSessions must be used within a PlaytestSessionProvider');
  }
  return context;
};

/**
 * Requests details for every id in `cardIds` that the session has not memoised.
 * Errors are not retried automatically; the retry control calls requestCardDetail.
 * Callers pass a memoised `cardIds` array.
 */
export const useEnsureCardDetails = (collectionId, session, cardIds) => {
  const { requestCardDetail } = usePlaytestSessions();
  useEffect(() => {
    const missing = new Set(cardIds.filter((cardId) => !session.details.has(cardId)));
    missing.forEach((cardId) => requestCardDetail(collectionId, cardId));
  }, [collectionId, session.details, cardIds, requestCardDetail]);
};
